/**
 * 担当開発案件一覧カード（メンバー詳細 / マイページ）。
 *
 * 見出しは当月pt 内訳バーと同じ橙黄（開発の主題色）。上に並ぶ「担当要件定義案件一覧」
 * （浅緑）と対にして、どちらの一覧かを色で読ませる。
 *
 * 担当中（完了・保留中以外）案件の平均健康度を、PL看板と同じ円形 rank ラベルでカード見出し右上に表示する。
 * 保留中の案件は着手が止まっており健康度が実態を表さないため、行の健康度バッジは出したまま
 * 総合健康度の母数からのみ外す。
 * 見出しラベルは担当案件テーブルの「ステータス」列見出しの中心に揃える（table-fixed で列位置が
 * 幅により動くため ResizeObserver で実測補正。テーブル自体のレイアウトは変更しない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー担当開発案件一覧カード。担当中PJ（完了・保留中を除く）の平均健康度をステータス列上に円形ラベルで表示し、見出しを開発の主題色（橙黄）で塗る
 * @graph-connects calcAvgHealth [calls] 担当中PJの平均健康度算出
 * @graph-connects MemberProjectsTable [calls] 担当案件テーブル描画
 * @graph-connects DEVELOP_BAR_COLOR [references] 見出しに使う開発の主題色（当月pt 内訳バーと共通）
 */

import { useMemo, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { MemberProjectsTable } from './MemberProjectsTable.js';
import { calcAvgHealth, HEALTH_COLORS } from '../health/index.js';
import { DEVELOP_BAR_COLOR } from '../member-pt-colors.js';
import type { MemberProjectListResponse } from '../../../types/project.js';

interface AssignedProjectsCardProps {
  /** メンバー担当プロジェクト一覧レスポンス。 */
  data: MemberProjectListResponse | undefined;
  /** 取得中フラグ。 */
  isLoading: boolean;
  /**
   * 見出しラベルの位置合わせを再計測するトリガー。本体JSXの描画タイミング（親のロード完了）が
   * データ取得と別のため、親でその値（例: memberData）を渡して ref 確定後の再整列を促す。
   */
  alignReadyKey?: unknown;
}

/** @graph-connects none */
export function AssignedProjectsCard({
  data,
  isLoading,
  alignReadyKey,
}: AssignedProjectsCardProps) {
  const { t } = useTranslation();

  // 担当中（完了・保留中以外）案件の平均健康度。保留中は着手が止まっており健康度が実態を
  // 表さないため、テーブルには残したまま総合健康度の母数からは外す。
  const avgHealth = useMemo(
    () =>
      calcAvgHealth(
        (data?.data ?? []).filter((p) => p.status !== '完了' && !p.status.startsWith('保留中')),
      ),
    [data],
  );

  // 平均健康度ラベルを「ステータス」列見出しの中心に揃える（実測して translateX 補正）
  const sectionRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const badge = badgeRef.current;
    if (!section || !badge) return;
    const align = () => {
      // MemberProjectsTable が公開する安定アンカー（列順が変わっても追従する）
      const statusTh = section.querySelector('table thead th[data-align-anchor="status"]');
      if (!statusTh) return;
      // 見出しテキストの中心（列は text-left なので box 中心ではなくテキスト中心を狙う）
      const range = document.createRange();
      range.selectNodeContents(statusTh);
      // Range.getBoundingClientRect 未実装環境（jsdom 等）では位置補正を graceful degrade する
      if (typeof range.getBoundingClientRect !== 'function') return;
      const titleRect = range.getBoundingClientRect();
      badge.style.transform = 'none';
      const badgeRect = badge.getBoundingClientRect();
      const shift = (titleRect.left + titleRect.right) / 2 - (badgeRect.left + badgeRect.right) / 2;
      badge.style.transform = `translateX(${String(shift)}px)`;
    };
    align();
    // レイアウト確定後にもう一度合わせる（初回描画時のフォント/テーブル確定ずれ対策）
    const raf = requestAnimationFrame(align);
    // ResizeObserver 未対応環境（jsdom 等）では初回 + rAF 測定のみで graceful degrade する
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        cancelAnimationFrame(raf);
      };
    }
    const ro = new ResizeObserver(align);
    ro.observe(section);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [avgHealth, data, isLoading, alignReadyKey]);

  return (
    <div ref={sectionRef}>
      <Card className="animate-content-reveal">
        <CardHeader
          title={
            <span style={{ color: DEVELOP_BAR_COLOR }}>{t('members.detail.assignedProjects')}</span>
          }
          action={
            avgHealth && (
              // ステータス列見出しの中心に揃える（位置は useLayoutEffect で実測して translateX 補正）
              <div ref={badgeRef} className="flex flex-col items-center gap-1">
                <span
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold"
                  style={{
                    backgroundColor: HEALTH_COLORS[avgHealth.grade].bg,
                    color: HEALTH_COLORS[avgHealth.grade].text,
                  }}
                  title={`${t(HEALTH_COLORS[avgHealth.grade].labelKey)} (${String(
                    avgHealth.score,
                  )}${t('projects.health.unit')})`}
                >
                  {avgHealth.grade}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t('members.healthDiagnosis.avgHealth')}
                </span>
                <span className="text-sm font-semibold text-[var(--color-text-secondary)]">
                  {String(avgHealth.score)}
                  {t('projects.health.unit')}
                </span>
              </div>
            )
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="loading-spinner" />
            </div>
          ) : data?.data ? (
            <MemberProjectsTable projects={data.data} />
          ) : (
            <p className="text-center py-8 text-[var(--color-text-muted)]">
              {t('members.detail.noProjects')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
